import { Controller } from "@hotwired/stimulus"

import RecaptchaController from "./recaptcha_controller"

import setupAutocompletePorts from "@/components/elm_autocomplete"
import { Elm } from "@/components/elm/EnquiryForm.elm"
import airbrakeClient, { logDecodingError } from "@/helpers/airbrake"
import { type GA4Event, pushGa4Event } from "@/helpers/ga4_helper"

type EnquiryFormPorts = {
  autocompleteBlurred: PortFromElm<null>
  autocompleteFocused: PortFromElm<FocusEvent>
  receiveFuse: PortToElm<AutocompleteData>
  sendFuse: PortFromElm<string>
  flagDecodingError: PortFromElm<void>
  pushGa4Event: PortFromElm<GA4Event>
  reportError: PortFromElm<Array<[string, Array<string>]>>
  requestRecaptcha: PortFromElm<void>
  recaptchaTokenReceived: PortToElm<string>
  recaptchaExpired: PortToElm<null>
}

export default class EnquiryFormController extends Controller<HTMLDivElement> {
  static outlets = ["recaptcha"]
  declare readonly recaptchaOutlet: RecaptchaController

  static targets = ["container"]
  declare readonly containerTarget: HTMLElement

  static values = { elmFlags: Object }
  declare readonly elmFlagsValue: Record<string, unknown>

  private app?: ElmApp<EnquiryFormPorts>

  connect(): void {
    this.app = Elm.EnquiryForm.init({
      node: this.containerTarget,
      flags: this.elmFlagsValue
    }) as ElmApp<EnquiryFormPorts>

    setupAutocompletePorts({
      app: this.app,
      data: this.elmFlagsValue.autocomplete as AutocompleteData
    })

    this.app.ports.flagDecodingError.subscribe(this.logFlagsError)
    this.app.ports.pushGa4Event.subscribe(pushGa4Event)
    this.app.ports.reportError.subscribe(this.notifyAirbrake)
    this.app.ports.requestRecaptcha.subscribe(this.executeRecaptcha)
  }

  disconnect(): void {
    this.app?.ports.flagDecodingError.unsubscribe(this.logFlagsError)
    this.app?.ports.pushGa4Event.unsubscribe(pushGa4Event)
    this.app?.ports.reportError.unsubscribe(this.notifyAirbrake)
    this.app?.ports.requestRecaptcha.unsubscribe(this.executeRecaptcha)
  }

  handleRecaptchaExpired(): void {
    this.app?.ports.recaptchaExpired.send(null)
  }

  handleRecaptchaTokenReceived({ detail: { token } }: CustomEvent<{ token: string }>): void {
    this.app?.ports.recaptchaTokenReceived.send(token)
  }

  private executeRecaptcha = (): void => {
    this.recaptchaOutlet.execute()
  }

  private logFlagsError = (): void => {
    logDecodingError(this.elmFlagsValue as Flags)
  }

  private notifyAirbrake = (errors: Array<[string, Array<string>]>): void => {
    void airbrakeClient.notify({
      error: "Failure submitting enquiry",
      params: Object.fromEntries(errors)
    })
  }
}
